import Modal from "components/DesignSystem/Modal/Modal";
import { useModal } from "hooks/useModal";
import {
  TaxActionItemType,
  useGetForm1099ListQuery,
} from "store/apis/actionItems";
import { actionIcons, ActionItemRowTrigger } from "./ActionItemRow";
import { Button } from "components/DesignSystem/Button/Button";
import { openLink } from "utils/openLink";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Card from "components/DesignSystem/Card/Card";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { EmptyIcon } from "components/icons/EmptyIcon";
import { ConditionalLink } from "components/conditionalLink";

export const Form1099Actions = ({
  item,
  groupId,
  open,
  onOpenChange,
  hideTrigger,
}: {
  item: TaxActionItemType & { custom_type: string };
  isFromChat?: boolean;
  groupId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}) => {
  const { custom_type: customType, title, subtitle, count } = item;
  const actionModal = useModal();
  const {
    data: itemList = [],
    isLoading,
    isSuccess,
  } = useGetForm1099ListQuery({ groupId }, { skip: !groupId });

  const isControlled = open !== undefined;
  const modalOpen = isControlled ? open : actionModal.isOpen;
  const handleOpenChange = onOpenChange ?? actionModal.toggle;

  if (item.ids?.length === 1 && !hideTrigger) {
    const taskId = item.ids[0];
    return (
      <ActionItemRowTrigger
        customType={customType}
        title={title}
        subtitle={subtitle}
        itemCount={count}
        handleCardClick={() => openLink(`/tax/filings/${taskId}`)}
      />
    );
  }

  return (
    <Modal.Root onOpenChange={handleOpenChange} open={modalOpen}>
      {!hideTrigger && (
        <Modal.Trigger asChild>
          <div>
            <ActionItemRowTrigger
              customType={customType}
              title={title}
              subtitle={subtitle}
              itemCount={count}
            />
          </div>
        </Modal.Trigger>
      )}
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>W-Forms Required</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            {...{
              isLoading,
              isEmpty: itemList?.length === 0,
              isSuccess,
            }}
            renderOnLoad={
              <Card.Body className="t-space-y-3">
                <Skeleton className="t-min-h-10" />
                <Skeleton className="t-min-h-10" />
                <Skeleton className="t-min-h-10" />
                <Skeleton className="t-min-h-10" />
              </Card.Body>
            }
          >
            <Async.Empty>
              <div className="t-flex t-justify-center t-items-center t-flex-col t-gap-3">
                <EmptyIcon />
                <p className="t-text-text-30 t-text-body-sm">No items</p>
              </div>
            </Async.Empty>
            <Async.Success>
              <div className="t-space-y-3">
                {itemList?.map((data) => (
                  <div
                    className="t-flex t-justify-between t-items-center t-border t-border-solid t-border-neutral-10 t-p-4 t-rounded-lg"
                    key={data.task_id}
                  >
                    <div className="t-flex t-gap-2 t-items-center">
                      <div className="t-relative t-min-h-10 t-min-w-10 t-max-h-10 t-max-w-10 t-rounded-full t-flex t-items-center t-justify-center t-bg-surface-lighter-grey">
                        {actionIcons[customType]}
                      </div>
                      <div>
                        <div className="t-text-subtext-sm t-text-text-100">
                          {data.count} {title}
                        </div>
                        <div className="t-text-body-sm t-text-text-30">
                          {data.entity_name} | {data.season}
                        </div>
                      </div>
                    </div>
                    <ConditionalLink to={`/tax/filings/${data.task_id}`}>
                      <span>
                        <Button size="small" rightIcon={<SmallRightArrow />}>
                          Go to filing
                        </Button>
                      </span>
                    </ConditionalLink>
                  </div>
                ))}
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
